import React from "react"
import { Link } from "gatsby"

import {
  EyeIcon
} from '@heroicons/react/outline'

import RankingTrophyIcon from "../common/rankingTrophyIcon"
import { readableNumber } from "../../utils/text"
import { formatTopicGrowthChange } from "../../utils/topics"

// import "../styles/topics.css"

// link is either to topic, or optionally topic on that platform
const TopicCard = ({
  topic,
  platform,
  feature,
  countOverride,
  mini,
  hidden,
  hideImage,
  topicRank,
  showPublicStats = true, // right now showing all the time, but just views
  fetchPriorityHigh = false,
}) => {
  const count = countOverride || topic.count
  const vwc = topic.viewsWithCommunities
  const monthlyViewsAll =
    (vwc != null && vwc !== '' ? Number(vwc) : Number(topic.views)) || 0
  const growth = formatTopicGrowthChange(topic, {
    onDark: !hideImage,
    wrapped: true,
  })
  const positiveGrowth = growth?.direction === "up" ? growth : null
  const showStatsRow =
    showPublicStats && (monthlyViewsAll > 0 || positiveGrowth)
  const monthlyViewsLabel = monthlyViewsAll
    ? `${readableNumber(monthlyViewsAll)} / mo`
    : ""

  const communitiesLabel = (
    <>
      {feature && feature.prefix ? <span>{feature.prefix} </span> : null}
      {platform && platform.slug !== 'independent' ? (
        <span>{count === 1 ? platform.groupName : platform.groupNamePlural} </span>
      ) : (
        <span>{count === 1 ? 'community' : 'communities'} </span>
      )}
      <span>{topic.people ? `for ${topic.people}` : ''}</span>
    </>
  )

  var link = `/topics/${topic.slug}/`
  if (platform) {
    link = `/topics/${topic.slug}/platform/${platform.slug}/`
  }

  return (
    <li
      key={topic.slug}
      className="relative overflow-visible"
      style={{
        display: hidden ? 'none' : 'block',
      }}
    >
      <Link
        to={link}
        className="group block overflow-visible rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-rose-500"
      >
        {!hideImage ? (
          <div className="relative w-full overflow-visible">
            <div
              className={`
                ${mini ? 'aspect-w-10 aspect-h-7' : 'aspect-w-10 aspect-h-5 sm:aspect-h-9'}
                relative w-full overflow-hidden rounded-lg bg-gray-100
              `}
            >
              <img
                src={topic.image}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
                {...(fetchPriorityHigh
                  ? { fetchpriority: "high" }
                  : { loading: "lazy" })}
              />
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  background: `
                  linear-gradient(180deg,
                    rgba(0,0,0,0.55) 0%,
                    rgba(0,0,0,0.38) 42%,
                    rgba(0,0,0,0.68) 100%)
                `,
                }}
                aria-hidden
              />
              <div className="absolute inset-0 flex flex-col justify-between p-4">
                <div className="w-full self-start pr-1">
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className={`
                        min-w-0 flex-1 text-shadow-card
                        ${count === 0 ? 'line-through opacity-60' : ''}
                        ${mini ? 'text-xl' : 'text-xl sm:text-2xl'}
                        line-clamp-2 font-semibold leading-snug text-white
                      `}
                    >
                      {topic.name}
                    </p>
                    {topicRank != null && topicRank <= 10 ? (
                      <span className="flex flex-shrink-0 items-center gap-1 text-lg font-bold leading-none text-white drop-shadow-md">
                        {topicRank <= 3 ? (
                          <span
                            className="inline-flex flex-shrink-0 [filter:drop-shadow(0_0_2px_#fff)_drop-shadow(0_0_5px_rgba(255,255,255,0.9))]"
                            aria-hidden
                          >
                            <RankingTrophyIcon rank={topicRank} className="h-5 w-5" />
                          </span>
                        ) : null}
                        <span className="whitespace-nowrap">#{topicRank}</span>
                      </span>
                    ) : null}
                  </div>
                  <p
                    className={`
                      text-shadow-card mt-0.5 line-clamp-2 font-medium leading-tight text-white
                      ${count === 0 ? 'line-through opacity-75' : 'opacity-90'}
                      ${mini ? 'text-base' : 'text-base sm:text-lg'}
                    `}
                  >
                    {count} {communitiesLabel}
                  </p>
                </div>
                <div className="w-full self-end">
                  {showStatsRow ? (
                    <div className="flex w-full flex-col items-end gap-1.5 text-white opacity-80">
                      {positiveGrowth ? (
                        <span
                          className={`text-shadow-card whitespace-nowrap text-sm font-medium leading-none ${positiveGrowth.className}`}
                        >
                          {positiveGrowth.label}
                        </span>
                      ) : null}
                      {monthlyViewsAll ? (
                        <div
                          className="flex min-w-0 items-center"
                          title="Monthly views, including community pages on this topic"
                        >
                          <EyeIcon
                            className="filter drop-shadow-lg mr-0.5 h-3.5 w-3.5 flex-shrink-0 text-white"
                            aria-hidden="true"
                          />
                          <span className="text-shadow-card whitespace-nowrap text-[11px] font-medium leading-none">
                            {monthlyViewsLabel}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
              <span className="sr-only">
                {topic.name}
                {`, ${count} communities${monthlyViewsAll ? `, ${readableNumber(monthlyViewsAll)} views per month including community pages` : ''}${positiveGrowth ? `, ${positiveGrowth.label} growth` : ''}`}
              </span>
            </div>
          </div>
        ) : null}

        {hideImage ? (
          <>
            <div className="pointer-events-none mt-1 flex items-center justify-between gap-2 overflow-visible">
              <p
                className={`
                  min-w-0 flex-1 truncate
                  ${count === 0 ? 'line-through opacity-50' : ''}
                  ${mini ? 'text-md' : 'text-lg '}
                  font-medium text-rose-500
                `}
              >
                {topic.name}
              </p>
              {topicRank != null && topicRank <= 10 ? (
                <span className="flex flex-shrink-0 items-center gap-1 text-sm font-bold text-gray-700">
                  {topicRank <= 3 ? <RankingTrophyIcon rank={topicRank} className="h-5 w-5" /> : null}
                  <span className="whitespace-nowrap">#{topicRank}</span>
                </span>
              ) : null}
            </div>
            <div className="pointer-events-none mt-0.5">
              <p className="block text-xs text-gray-500">
                <span>{count} </span>
                {communitiesLabel}
              </p>
              {showStatsRow ? (
                <div className="mt-1 flex flex-col items-end gap-1.5">
                  {positiveGrowth ? (
                    <span className={`text-sm font-medium leading-none ${positiveGrowth.className}`}>
                      {positiveGrowth.label}
                    </span>
                  ) : null}
                  {monthlyViewsAll ? (
                    <div
                      className="flex items-center space-x-1.5 opacity-80"
                      title="Monthly views, including community pages on this topic"
                    >
                      <EyeIcon
                        className="h-4 w-4 flex-shrink-0 text-gray-300"
                        aria-hidden="true"
                      />
                      <p className="text-[11px] leading-tight text-gray-500">
                        {monthlyViewsLabel}
                      </p>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </>
        ) : null}
      </Link>
    </li>
  )
}

export default TopicCard
